import React from "react";
import { Form } from "antd";
import { Store } from "antd/lib/form/interface";
import { useMutation } from "react-query-legacy";
import { message } from "antd";
import { BillingEngineService } from "services";
import { useEditServiceContext, useRateCardContext } from "../contextProviders";
import { useCurrentVersion } from "../hooks";
import { ServiceFormWrapper, ServiceRate } from "../_common";
import PostageFeeInput from "./PostageFeeInput/PostageFeeInput";
import PickFeeSection from "./PickFeeSection";
import { serializedFormSchema, deserializedFormSchema } from "./formSchema";
import { INPUT_NEEDED, notifyError } from "./PickFeeSection/Helpers";
import { PickPackRate, PostageFee } from "./interfeces";
import { PickFeeSectionStyle } from "./PickFeeSection/PickFeeSection.styles";

const PickAndPackRates = () => {
  const { setAllowEditing, setSaveValidationFailed, isSaving, setIsSaving } = useEditServiceContext();
  const [form] = Form.useForm();
  // Current rate card
  const { rateCardId, activeZone, dispatch, status, isLoadingZones } = useRateCardContext();
  const { data: currentVersion, status: currentVersionLoadingStatus } = useCurrentVersion(activeZone);
  const initialFormValues = currentVersion?.data;

  // Creates version
  const [createVersion, { status: createVersionStatus }] = useMutation(BillingEngineService.updateZoneRateCardVersion, {
    onSuccess: (data) => {
      dispatch({
        type: "CREATE_VERSION",
        version: data,
      });
    },
  });

  const onFinish = (values: Store) => {
    if (activeZone) {
      const versionData = serializedFormSchema.cast(values);

      createVersion({
        rateCardId: activeZone.rateCardId,
        rateCardZoneId: activeZone.id,
        version: versionData,
      })
        .then(() => {
          message.success("New version created.");
        })
        .finally(() => {
          setAllowEditing(false);
          setIsSaving(false);
        });
    }
  };

  const onReset = () => {
    form.resetFields();
    setSaveValidationFailed(false);
    form.setFieldsValue(deserializedFormSchema.cast(initialFormValues));
  };

  /**
   * Effect that handle form values populations
   * whenever one of dependencies changes
   */
  React.useEffect(() => {
    // Cleanup form every time
    if (form) {
      form.resetFields();
    }

    if (currentVersion && currentVersion.data) {
      form.setFieldsValue(deserializedFormSchema.cast(currentVersion.data));
    }
  }, [currentVersion, currentVersionLoadingStatus, rateCardId, form]);

  const isFormDataValid = (pickFee: PickPackRate) => {
    let validData = true;
    if (!pickFee) {
      return false;
    }

    pickFee.weights.forEach((wt) => {
      if (wt.to === INPUT_NEEDED) {
        validData = false;
      }
      wt.tiers.forEach((t) => {
        if (t.to === INPUT_NEEDED) {
          validData = false;
        }
        t.picks.forEach((p) => {
          if (p.to === INPUT_NEEDED) {
            validData = false;
          }
        });
      });
    });

    return validData;
  };

  const isPostageFeeDataUnique = (postageFee: PostageFee[]) => {
    const uniqueCarriers = new Set(postageFee.map((x) => x.carrier));
    return uniqueCarriers.size === postageFee.length;
  };

  return (
    <ServiceRate serviceName="Pick & Pack">
      <ServiceFormWrapper
        saving={isSaving}
        loading={
          currentVersionLoadingStatus === "loading" ||
          createVersionStatus === "loading" ||
          status === "loading" ||
          isLoadingZones
        }
        onSave={() => {
          const pickFee = form.getFieldValue("pickFee");
          const postageFeeList: PostageFee[] = form.getFieldValue("postageFeeList");
          if (pickFee && isFormDataValid(pickFee) && postageFeeList && isPostageFeeDataUnique(postageFeeList)) {
            setIsSaving(true);
            form.submit();
          } else {
            setSaveValidationFailed(true);
            if (postageFeeList && !isPostageFeeDataUnique(postageFeeList)) {
              notifyError("Duplicate carrier-markup is not allowed");
            }
            if (!pickFee) {
              notifyError("You have not added values for the pick fee");
            }
            if (!postageFeeList) {
              notifyError("You have not added values for the postage fee markup");
            }
          }
        }}
        onCancel={onReset}
        title="Admin Fees"
      >
        <Form hideRequiredMark name="pickPack" form={form} onFinish={onFinish} initialValues={initialFormValues}>
          <Form.Item name="postageFee" label="Postage Processing Fee">
            <PostageFeeInput />
          </Form.Item>
          <PickFeeSectionStyle>
            <PickFeeSection form={form} />
            <Form.Item name="pickFee" />
          </PickFeeSectionStyle>
        </Form>
      </ServiceFormWrapper>
    </ServiceRate>
  );
};

export default PickAndPackRates;
